import axios from 'axios'
import * as Sentry from '@sentry/vue'

/** How long a report may take before the ad stops waiting on it. */
const REPORT_TIMEOUT_MS = 5000

/**
 * A report the ad stopped waiting for.
 *
 * Worth a line of its own, because the cutoff below hides the very failure it
 * exists to survive: the ad now finishes on time either way, so from the layer
 * nothing looks wrong, and the console it warns into is on the streamer's
 * machine. Grouped by which report it was rather than by streamer — the question
 * is whether an endpoint has started hanging, not for whom.
 */
function reportTimedOut (label) {
  try {
    Sentry.captureMessage(`ad report timed out: ${label}`, {
      level: 'warning',
      fingerprint: ['ad-report-timeout', label],
      tags: { ad_report: label },
      extra: { timeoutMs: REPORT_TIMEOUT_MS }
    })
  } catch (e) {
    // Sentry is not initialised when VUE_APP_ENVIRONMENT is local, and a
    // transport that is down is not a reason to take the ad with it.
    void e
  }
}

/**
 * Tell the backend that an ad went on screen, or that it finished.
 *
 * Both are signed GET routes, so navigator.sendBeacon is out — it only speaks
 * POST. fetch with keepalive is the closest equivalent: the request survives
 * the page being torn down, which is exactly when the finish report is sent.
 *
 * This is the only word the backend ever gets. TaskExecuted is published into
 * the broker and nothing acknowledges it, so a report that quietly fails to
 * leave the machine is indistinguishable from an ad that never played. That is
 * why every path here ends in a fallback rather than an unhandled rejection —
 * VideoBox and FullScreenBannerBox each grew their own copy of this, and
 * OneThirdBannerBox, which renders every one-third-banner and every
 * non-fullscreen extension-universal, had none.
 *
 * @param {string} url   signed route to fetch; falsy urls are a no-op, which is
 *                       what a layer running against an older backend sees
 * @param {string} label what to call this in the console when it fails
 * @returns {Promise<void>} resolves whether or not the report got through
 */
export async function reportAdEvent (url, label = 'ad report') {
  if (!url) return

  if (typeof fetch === 'function') {
    // Bounded, and that bound is the point. FullScreenBannerBox awaits this
    // before it emits `finished`, so an end report that never settles is an ad
    // that never finishes: the countdown stays on screen, and on a one-third
    // banner the looping creative keeps its audio going until the streamer
    // resets the source. The axios fallback below has always had a timeout; the
    // fetch path that superseded it did not.
    //
    // AbortController rather than AbortSignal.timeout, which OBS builds on older
    // CEF do not have.
    const abort = new AbortController()
    const cutoff = setTimeout(() => abort.abort(), REPORT_TIMEOUT_MS)

    try {
      await fetch(url, { method: 'GET', keepalive: true, signal: abort.signal })
      return
    } catch (e) {
      // A report we gave up on is not one axios should spend another timeout
      // retrying; the ad is already waiting on us.
      if (abort.signal.aborted) {
        console.warn(`${label} report timed out after ${REPORT_TIMEOUT_MS}ms`)
        reportTimedOut(label)
        return
      }
      console.warn(`${label} fetch failed, falling back to axios`, e)
    } finally {
      clearTimeout(cutoff)
    }
  }

  try {
    await axios.get(url, { timeout: REPORT_TIMEOUT_MS })
  } catch (e) {
    console.warn(`${label} report failed`, e)
  }
}

export default reportAdEvent
